import { DecimalPipe } from "@angular/common";
import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";

/**
 * Generated class for the ConversionInputComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */

@Component({
  selector: "length-input",
  templateUrl: "length-input.html",
})
export class LengthInputComponent implements OnInit, AfterViewInit {
  // Configuration properties

  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() group: FormGroup;
  @Input() config;

  // Internal data model
  id: string;
  componentId: string;
  label: any;
  placeholder: string = "";
  disabled: boolean = false;
  readonly: boolean = false;
  max: number;
  min: number;
  convertedMax: string;
  convertedMin: string;
  required: boolean;
  feet: number = 0;
  inches: number;
  combinedInches: number;
  cm: number;
  inchesConversionRatio: number;
  cmConversionRatio: number;
  lastInches: number;
  lastCm: number;
  precision: string;
  errors: object = null;
  touched: boolean = false;

  constructor(
    public formBuilder: FormBuilder,
    public decimalPipe: DecimalPipe,
    public dataService: AssessmentDataService
  ) {
    // console.log('Hello ConversionInputComponent Component');
  }

  ngOnInit(): void {
    // console.log( "Group: " );
    // console.log( this.group );
    // console.log( "Conversion Input config: " );
    // console.log( this.config );

    this.id = this.config.id;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined ? this.config.label : this.config.id;
    if (this.label === "false") {
      this.label = false;
    }
    this.readonly = this.config.readonly;
    if (this.config.placeholder) {
      this.placeholder = this.config.placeholder;
    }

    this.inchesConversionRatio = 2.54;
    this.cmConversionRatio = 1 / this.inchesConversionRatio;

    this.precision = this.config.rules.precision || "1.0-2";

    if (this.config.rules) {
      if (this.config.rules.required) {
        this.required = this.config.rules.required;
      }

      if (this.config.rules.min !== undefined) {
        this.min = this.config.rules.min;
        this.convertedMin = this.decimalPipe.transform(
          this.min * this.inchesConversionRatio,
          this.precision
        );
      }

      if (this.config.rules.max !== undefined) {
        this.max = this.config.rules.max;
        this.convertedMax = this.decimalPipe.transform(
          this.max * this.inchesConversionRatio,
          this.precision
        );
      }
    }

    // Outer form state
    this.group.addControl(this.id, this.formBuilder.control({}));
    let selfControl = this.group.controls[this.id];

    selfControl.valueChanges.subscribe((data) => {
      this.loadState(data);
    });

    this.loadState(this.group.controls[this.id].value);
    this.syncInnerValue();
  }

  ngAfterViewInit(): void {
    this.updateErrors();
    this.group.controls[this.id].setErrors(this.errors);

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.updateErrors();
      this.group.controls[this.id].setErrors(this.errors);
    });
  }

  loadState(data) {
    this.disabled = this.group.controls[this.id].disabled;

    if (data === null) {
      this.inches = null;
      this.feet = null;
    } else if (this.combinedInches != data["inches"]) {
      this.combinedInches = data["inches"];
      this.convertCombinedInches();
    }
  }

  syncInnerValue(): void {
    this.group.controls[this.id].setValue(this.getValue());
    this.group.controls[this.id].markAsDirty();

    this.updateErrors();
    this.group.controls[this.id].setErrors(this.errors);
  }

  feetChange(event) {
    this.feet = event;
    this.convertFeetInches();
    this.touched = true;
  }

  inchesChange(event) {
    this.inches = event;
    this.convertFeetInches();
    this.touched = true;
  }

  cmChange(event) {
    this.cm = event;
    this.convertCm();
    this.touched = true;
  }

  convertCombinedInches() {
    let feet = +this.decimalPipe
      .transform(Math.floor(this.combinedInches / 12), this.precision)
      .replace(/\,/g, "");
    let inches = +this.decimalPipe
      .transform(this.combinedInches % 12, this.precision)
      .replace(/\,/g, "");

    this.feet = feet;
    this.inches = inches;

    this.convertFeetInches();
  }

  convertFeetInches() {
    if (!this.feet) {
      this.feet = 0;
    }
    if (!this.inches) {
      this.inches = 0;
    }

    let combinedInches = this.feet * 12 + this.inches * 1;

    if (this.lastInches != combinedInches) {
      this.combinedInches = combinedInches;
      let converted = +this.decimalPipe
        .transform(this.inchesConversionRatio * combinedInches, this.precision)
        .replace(/\,/g, "");

      this.lastCm = converted;
      this.cm = converted;
      this.syncInnerValue();
    }
  }

  convertCm() {
    if (!this.cm) {
      this.cm = 0;
    }

    if (this.lastCm != this.cm) {
      let converted = +this.decimalPipe
        .transform(this.cmConversionRatio * this.cm, this.precision)
        .replace(/\,/g, "");
      this.lastInches = converted;
      this.combinedInches = converted;
      this.convertCombinedInches();
      this.syncInnerValue();
    }
  }

  getValue() {
    let val = {};
    val["inches"] = this.combinedInches;
    val["cm"] = this.cm;
    return val;
  }

  updateErrors() {
    let errors = {};
    if (this.required && !this.combinedInches) {
      errors["required"] = true;
    }
    if (this.max !== undefined && this.combinedInches > this.max) {
      errors["max"] = true;
    }

    if (this.min !== undefined && this.combinedInches < this.min) {
      errors["min"] = true;
    }

    if (Object.keys(errors).length < 1) {
      errors = null;
    }

    this.errors = errors;
    // console.log( "Updated errors: ", this.errors );
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/length-input/length-input.ts